import type { ReactElement } from "react"

import type { ScatterPlotRawSerie } from "@nivo/scatterplot"
import { useFormikContext } from "formik"
import { truncate } from "lodash"

import { ChartsContainer } from "../../../../../components/graphs/ChartsContainer"
import { BaseScatterPlot } from "../../../../../components/graphs/ScatterPlot/BaseScatterPlot"
import { getTheme } from "../../../../../hooks/UseTheme"
import type { ConfigFormikProps } from "../../../Interfaces"

type CorrelationFunnelProps = {
  chartData: ScatterPlotRawSerie<{
    x: number
    y: string
    bin: string
    decision?: string
    reason?: string
  }>[]
  title: string
  isLoading: boolean
  graphHeight: number
  tooltipText?: string
}

/**
 * A component to display a scatter plot of a correlation funnel.
 *
 * It renders a scatter plot with x-axis representing the "bin" values,
 * y-axis representing the "y" values, and size of the circles representing the
 * "x" values.
 *
 * @param {CorrelationFunnelProps} props - The props for the CorrelationFunnel
 * component.
 * @returns {ReactElement} A Grid item containing the CorrelationFunnel
 * visualization.
 */
export function CorrelationFunnel(props: Readonly<CorrelationFunnelProps>): ReactElement {
  const { chartData, title, isLoading, graphHeight, tooltipText } = props

  const {
    values: { positiveLabel, negativeLabel },
  } = useFormikContext<ConfigFormikProps>()

  const theme = getTheme()

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight}
      tooltipText={tooltipText}
      gutterBottom
    >
      <BaseScatterPlot
        data={chartData}
        margin={{ top: 20, bottom: 40, left: 130, right: 10 }}
        axisTop={{
          format: (value) => {
            return value === -1 ? negativeLabel?.name : value === 1 ? positiveLabel?.name : ""
          },
        }}
        renderTickFn={({ value, y, textX, textAnchor }) => (
          <g>
            <title>{value}</title>
            <text
              dominantBaseline="central"
              textAnchor={textAnchor}
              x={textX}
              y={y}
              style={{
                fontFamily: "sans-serif",
                fontSize: "11px",
                fill: theme.palette.base.text[1],
                outlineWidth: "0px",
                outlineColor: "transparent",
              }}
            >
              {truncate(value, { length: 20 })}
            </text>
          </g>
        )}
      />
    </ChartsContainer>
  )
}
